import { DegreeChord, DegreeChordWithMeta } from "@/composables/useChords";

export function deepStripUndefined<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj.map(deepStripUndefined) as any;
  }
  if (obj && typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([, v]) => v !== undefined)
        .map(([k, v]) => [k, deepStripUndefined(v)])
    ) as T;
  }
  return obj;
}

export const pruneUndefined = <T extends Record<string, any>>(obj: T) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as T;


export type LegacyChord = Record<string, unknown>;


const isInt = (v: any) => Number.isInteger(v);

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));
const toInt = (v: any, fallback = 0) => {
  const n = typeof v === 'string' ? parseInt(v, 10) : Number(v);
  return Number.isFinite(n) ? n|0 : fallback;
};
const normalizeBass = (b: any): DegreeChord['bassDegree'] | undefined => {
  const n = toInt(b, 0); // default to 0 if undefined/null/NaN
  return n > 0 && n <= 7 ? (n as DegreeChord['bassDegree']) : undefined;
};




export function isDegreeChordWithMeta(x: any): x is DegreeChordWithMeta {
  return x && typeof x === 'object'
    && isInt(x.primary) && x.primary >= 1 && x.primary <= 7
    && typeof x.quality === 'string'
    && isInt(x.accidental) && x.accidental >= -2 && x.accidental <= 2
    && isInt(x.chapterId)
    && isInt(x.letterIndex)     // <- strong discriminant
    && isInt(x.lineIndex);      // <- strong discriminant
}

export function isLegacyChord(x: any): boolean {
  if (!x || typeof x !== 'object') return false;
  return (
    'minor' in x ||                       // legacy flag instead of quality
    'bass' in x ||
    typeof x.letterIndex === 'string' ||  // indices as strings
    typeof x.lineIndex === 'string' ||
    'id' in x                             // id embedded in object
  );
}

export function legacyToDegreeChordWithMeta(x: any): DegreeChordWithMeta {
  return {
    primary: clamp(toInt(x.primary, 1), 1, 7) as 1|2|3|4|5|6|7,
    quality: x.minor ? 'm' : 'M',                  // map legacy boolean
    accidental: 0,
    id: toInt(x.id),
    add: x.add ?? undefined,
    bassDegree: normalizeBass(x.bass),
    bassAccidental: 0,
    chapterId: toInt(x.chapterId, 0),
    letterIndex: toInt(x.letterIndex ?? 0, 0),    // strings → numbers
    lineIndex: toInt(x.lineIndex ?? 0, 0),
  };
}

// export function sanitizeChord(c: any): DegreeChordWithMeta {
//   const primary = clamp(toInt(c.primary, 1), 1, 7) as 1|2|3|4|5|6|7;
//   const accidental = clamp(toInt(c.accidental ?? 0), -2, 2) as Accidental;
//   const bassAccidental = clamp(toInt(c.bassAccidental ?? 0), -2, 2) as Accidental;

//   return {
//     ...c,
//     primary,
//     accidental,
//     bass: normalizeBass(c.bass),
//     bassAccidental,
//     letterIndex: toInt(c.letterIndex, 0),
//     lineIndex: toInt(c.lineIndex, 0),
//   };
// }